<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :storage-provider="storageProvider"
      :storage-space="storageSpace"
      :tenant-filter="tenantFilter"
      :tenant-storage-provider="tenantStorageProvider"
      :desabled="desabled"
      v-if="showFilters"
    />

    <b-row v-if="showFilters">
      <b-col cols="6">
        <breadcrumb
          :nameNav="nameNav"
          :itemFilter="itemFilter"
          :searchQuery="searchQuery"
          @itemFilterSearch="addItemFilterSearch"
        />
      </b-col>
      <b-col cols="6" class="pr-3 text-right filters-col">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <filters
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :storage-provider.sync="storageProvider"
          :storage-provider-filter.sync="storageProviderFilter"
          :size-options="sizeOptions"
          :assigned-space-min-type.sync="assignedSpaceMinType"
          :assigned-space-max-type.sync="assignedSpaceMaxType"
          :assigned-space-min.sync="assignedSpaceMin"
          :assigned-space-max.sync="assignedSpaceMax"
          :used-space-min-type.sync="usedSpaceMinType"
          :used-space-max-type.sync="usedSpaceMaxType"
          :used-space-min.sync="usedSpaceMin"
          :used-space-max.sync="usedSpaceMax"
        />
      </template>
    </filter-modal>

    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card :class="{ 'm-2': showFilters, 'm-0': !showFilters }" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              data-cy="automatest-v-select-pagination-control"
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6" v-if="showFilters">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                data-cy="search"
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'Filesystem')"
              >
                <span class="text-nowrap text-capitalize">{{
                  $t("actions.add") + " " + $tc("tenants.title", 1) + " " + $t("tenants.store.storage_provider")
                }}</span>
              </b-button>
            </div>
          </b-col>
          <!--<b-col cols="12" md="1">
            <div class="d-flex align-items-center justify-content-start">
              <b-button
                @click="exportExcel()"
                variant="success"
                class="text-capitalize"
                type="button"
                :disabled="buttonExport"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
                <span class="text-nowrap text-capitalize"> </span>
              </b-button>
            </div>
          </b-col>-->
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #cell(assigned_space)="data">
          {{ convertirBytesGbTb(data.item.assigned_space) }}
        </template>

        <template #cell(used_space)="data">
          {{ convertirBytesGbTb(data.item.used_space) }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data" v-if="showFilters">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              data-cy="actions"
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                @click="openEdit(data.item)"
                v-if="$can('update', 'Filesystem')"
                class="border-0"
                button
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>
              <b-list-group-item
                @click="confirmDelete(data.item.id)"
                v-if="$can('delete', 'Filesystem')"
                class="border-0"
                button
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            data-cy="pagination"
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BImg,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import router from "@/router";
import Breadcrumb from "@/components/Breadcrumb.vue";

export default {
  components: {
    Sidenav,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BImg,
    vSelect,
    Breadcrumb,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
  },
  props: {
    filters: {
      type: Object | Array,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const update = (data) => {
      currentPage.value = data;
    };

    const loadingToast = ref(null);
    const action = ref("list");
    const { t, tc } = useI18nUtils();
    const trans = {
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "tenant.title": tc("tenants.title", 1),
      report_of: t("messages.export.report"),
      full_download: t("messages.export.full_download"),
    };

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const desabled = ref(false);
    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    const toast = useToast();

    const itemFilter = ref()
    const addItemFilter = (data) => {
      itemFilter.value = data
    };
    const addItemFilterSearch = (data) => {
      data.forEach((element) => {
        if (nameNav.value === element.text) {
          searchQuery.value = element.searchQuery
        }
      })
    }

    const nameNav = ref("breadcrumbs.tenants.storage_provider");

    const {
      tenantStorageProvider,
      tenantOptions,
      storageSpace,
      storageProvider,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      buildFilters,
      clearSearchFilters,
      filtersCount,
      tenantsStorageOptions,
      tenantFilter,
      storageProviderFilter,
      sizeOptions,
      assignedSpaceMinType,
      assignedSpaceMaxType,
      assignedSpaceMin,
      assignedSpaceMax,
      usedSpaceMinType,
      usedSpaceMaxType,
      usedSpaceMin,
      usedSpaceMax,
    } = useList(props);

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("tenant_storage_provider/delete", id)
            .then((response) => {
              if (response.status == 204) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: context.parent.$i18n.t("tenants.message.delete"),
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });

              setTimeout(() => {
                self.refetchData();
              }, 500);

              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: context.parent.$i18n.t("tenants.message.error"),
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            })
            .finally(() => {
              loadingToast.value.close();
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const tenantFile = function (id) {
      router.push({ path: `/tenant_files/${id}` });
    };

    const showStorageProvider = function (id) {
      router.push({ path: `/tenant_storage_provider/${id}` });
    }

    const convertirBytesGbTb = (bytes) => {
      const UN_MB = 1024 ** 2;
      const UN_GB = 1024 ** 3;
      const UN_TB = 1024 ** 4;

      if (bytes >= UN_TB) {
        return `${(bytes / UN_TB)} TB`; //`${(bytes / UN_TB).toFixed(2)} TB`;
      }
      if (bytes >= UN_GB) {
        return `${(bytes / UN_GB)} GB`;
      }
      if (bytes >= UN_MB) {
        return `${(bytes / UN_MB)} MB`;
      }
      return `${(bytes / UN_TB)} B`;
    }

    return {
      tenantFile,
      loadingToast,
      action,
      //exportExcel,
      //buttonExport,
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      desabled,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      storageProvider,
      storageSpace,
      tenantOptions,
      tenantsStorageOptions,
      tenantFilter,
      storageProviderFilter,
      sizeOptions,
      assignedSpaceMinType,
      assignedSpaceMaxType,
      assignedSpaceMin,
      assignedSpaceMax,
      usedSpaceMinType,
      usedSpaceMaxType,
      usedSpaceMin,
      usedSpaceMax,
      showStorageProvider,
      convertirBytesGbTb,
      tenantStorageProvider,

      nameNav,
      addItemFilterSearch,
      addItemFilter,
      itemFilter,
    };
  },
};
</script>

<style lang="scss">
.hoverable:hover {
  background: gray;
}
</style>
